// Chat sidebar — extends shared-gov's AppSidebar shell so the chrome (width,
// background, border, scrollable people section, person flyout) is identical
// to calendar's sidebar. The slot extends fill in chat-specific bits:
//
//   Top    → "New message" button
//   Middle → CHANNELS / DIRECT MESSAGES / EXTERNAL PEOPLE groups
//
// Channel rows show unread counts via UnreadBadge; the active row is themed.
//
// Each group in Middle is a `ChatSidebarGroup` instance — the wrapper handles
// collapse state reading, the collapsible Header, and the List hide pattern.
// Each instance supplies local state (groupKey, label, addAction) plus its
// own List.childExtends + children factory.
import {
  channelDisplayName,
  buildDMChannels,
  unreadCount,
  mutedChannels
} from '../../functions/chat/chatUtils.js'

const _getUnread = (s) => {
  if (!s?.id || !s?.root) return 0
  const me = s.root.currentUserEmail
  const msgs = s.root.messagesByChannel?.[s.id] || []
  const members = s.root.channelMembers || []
  const membership = members.find(m => m.channel_id === s.id && m.member_email === me)
  return unreadCount(msgs, membership?.last_read_at, me)
}

const _getMentionCount = (s) => {
  if (!s?.id || !s?.root) return 0
  const mentions = s.root.myMentions || []
  return mentions.filter(m => m.channel_id === s.id && !m.read_at).length
}

const _isMuted = (s) => {
  if (!s?.id || !s?.root) return false
  return mutedChannels(s.root.channelMembers, s.root.currentUserEmail).has(s.id)
}

// Trailing bell-slash icon shown on muted conversations. Fades the row
// to signal "notifications off".
const _muteIndicator = {
  hide: (el, s) => !_isMuted(s),
  tag: 'span',
  fontSize: 'Y2',
  color: 'caption',
  marginLeft: 'Y',
  opacity: '.7',
  text: '\u{1F515}'  // 🔕
}

const _unreadBadge = {
  hide: (el, s) => _getUnread(s) === 0,
  tag: 'span',
  padding: 'V Z',
  round: 'B',
  fontSize: 'Y1',
  fontWeight: '700',
  background: 'blue',
  color: 'white',
  marginLeft: 'auto',
  text: (el, s) => {
    const n = _getUnread(s)
    return n > 99 ? '99+' : String(n)
  }
}

const _mentionBadge = {
  hide: (el, s) => _getMentionCount(s) === 0,
  tag: 'span',
  padding: 'V Z',
  round: 'B',
  fontSize: 'Y1',
  fontWeight: '700',
  background: 'red',
  color: 'white',
  marginLeft: 'Y',
  text: '@'
}

export const ChatSidebar = {
  extends: 'AppSidebar',
  PeopleSection: null,
  position: 'relative',
  minWidth: 'F3',
  maxWidth: '50dvw',

  ResizeHandle: {
    ignoreChildExtends: true,
    position: 'absolute',
    top: '50%',
    right: 'W2',
    width: 'W2',
    height: 'C1',
    cursor: 'col-resize',
    zIndex: '10',
    transform: 'translateY(-50%)',
    background: 'overlayStrong',
    ':hover': { background: 'blue.2' },
    onCreate: (el) => {
      if (!el.node) return
      el.node.addEventListener('mousedown', (e) => {
        e.preventDefault()
        const sidebar = el.node.parentElement
        if (!sidebar) return
        const startX = e.clientX
        const startW = sidebar.offsetWidth
        const onMove = (ev) => {
          const w = Math.min(
            Math.max(startW + ev.clientX - startX, 200),
            window.innerWidth * 0.5
          )
          sidebar.style.width = w + 'px'
        }
        const onUp = () => {
          document.removeEventListener('mousemove', onMove)
          document.removeEventListener('mouseup', onUp)
        }
        document.addEventListener('mousemove', onMove)
        document.addEventListener('mouseup', onUp)
      })
    }
  },

  Top: null,

  Middle: {
    flow: 'y',
    gap: 'B',
    flex: '1',
    overflow: 'auto',

    // Pinned channels/DMs — only visible when there are pinned items
    PinnedGroup: {
      extends: 'ChatSidebarGroup',
      display: (el, s) => {
        const pinned = s?.root?._pinnedChannels || []
        return pinned.length > 0 ? 'flex' : 'none'
      },
      state: {
        groupKey: 'PINNED',
        label: 'PINNED',
        addAction: null
      },
      List: {
        childExtends: 'ChatPinnedRow',
        children: (el, s) => {
          const root = s && s.root
          if (!root) return []
          const pinnedIds = new Set(root._pinnedChannels || [])
          return (root.channels || []).filter(c => pinnedIds.has(c.id))
        }
      }
    },

    ChannelsGroup: {
      extends: 'ChatSidebarGroup',
      state: {
        groupKey: 'CHANNELS',
        label: 'CHANNELS',
        addAction: 'new-channel'
      },
      List: {
        childExtends: 'ChatChannelRow',
        children: (el, s) => {
          const root = s && s.root
          if (!root) return []
          const me = root.currentUserEmail
          const hidden = new Set(root._hiddenChannels || [])
          const myIds = new Set(
            (root.channelMembers || [])
              .filter((m) => m.member_email === me)
              .map((m) => m.channel_id)
          )
          const seen = new Set()
          return (root.channels || []).filter((c) => {
            if (c.kind !== 'channel') return false
            if (!myIds.has(c.id)) return false
            if (hidden.has(c.id)) return false
            if (seen.has(c.id)) return false
            seen.add(c.id)
            return true
          })
        }
      }
    },

    DMsGroup: {
      extends: 'ChatSidebarGroup',
      state: {
        groupKey: 'DIRECT_MESSAGES',
        label: 'DIRECT MESSAGES',
        addAction: 'new-message'
      },
      List: {
        childExtends: 'ChatDMRow',
        children: (el, s) => {
          const root = s && s.root
          const hidden = new Set(root?._hiddenChannels || [])
          return buildDMChannels(root, false)
            .filter(c => c.kind === 'dm' && !hidden.has(c.id))
        }
      }
    },

    GroupsGroup: {
      extends: 'ChatSidebarGroup',
      display: (el, s) => {
        const root = s && s.root
        if (!root) return 'none'
        const hidden = new Set(root._hiddenChannels || [])
        const hasGroups = buildDMChannels(root, false)
          .some(c => c.kind === 'group_dm' && !hidden.has(c.id))
        return hasGroups ? 'flex' : 'none'
      },
      state: {
        groupKey: 'GROUPS',
        label: 'GROUPS',
        addAction: 'new-group'
      },
      List: {
        childExtends: 'ChatGroupRow',
        children: (el, s) => {
          const root = s && s.root
          const hidden = new Set(root?._hiddenChannels || [])
          return buildDMChannels(root, false)
            .filter(c => c.kind === 'group_dm' && !hidden.has(c.id))
        }
      }
    },

    ExternalGroup: {
      extends: 'ChatSidebarGroup',
      state: {
        groupKey: 'EXTERNAL',
        label: 'EXTERNAL PEOPLE',
        addAction: 'new-external-message'
      },
      List: {
        childExtends: 'ChatDMRow',
        children: (el, s) => {
          const root = s && s.root
          const hidden = new Set(root?._hiddenChannels || [])
          return buildDMChannels(root, true).filter(c => !hidden.has(c.id))
        }
      }
    }
  }
}

// Collapsible-group wrapper used by every group in ChatSidebar.Middle.
// Reads `groupKey`, `label`, and `addAction` from its own (locally-merged)
// state; reads collapse state via `root.collapsedGroups[groupKey]`.
export const ChatSidebarGroup = {
  flow: 'y',
  gap: 'Z',

  Header: {
    extends: 'ChatSidebarGroupHeader'
  },

  List: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) =>
      !!(
        s &&
        s.root &&
        s.root.collapsedGroups &&
        s.root.collapsedGroups[s.groupKey]
      ),
    childrenAs: 'state'
  }
}

// Reusable collapsible-group header (Caret + Label + AddBtn).
// Reads all variable content from local state via the group wrapper.
export const ChatSidebarGroupHeader = {
  flow: 'x',
  align: 'center left',
  gap: 'X2',
  cursor: 'pointer',
  padding: 'V 0',
  attr: {
    'data-toggle-group': (el, s) => s && s.groupKey
  },

  Caret: {
    extends: 'Icon',
    icon: 'arrowAngleDown',
    boxSize: 'Z',
    color: 'caption',
    // The Icon base resolves `el.icon` once in `onInit` and bakes the SVG
    // into `el.src`, so a reactive `icon: (el, s) => ...` factory never
    // re-runs when state flips. Rotating a single icon via the reactive
    // `transform:` CSS factory IS picked up by the effect pipeline — and
    // animates smoothly as a bonus. Collapsed → arrow points right.
    transform: (el, s) =>
      s &&
      s.root &&
      s.root.collapsedGroups &&
      s.root.collapsedGroups[s.groupKey]
        ? 'rotate(-90deg)'
        : 'rotate(0deg)',
    transition: 'transform 120ms ease'
  },
  CaptionTitle: {
    fontSize: 'Y2',
    flex: '1',
    Text: {
      text: (el, s) => (s && s.label) || ''
    }
  },
  AddBtn: {
    tag: 'span',
    fontSize: 'A',
    color: 'caption',
    padding: '0 V',
    text: '+',
    attr: {
      'data-action': (el, s) => s && s.addAction
    },
    ':hover': { color: 'title' }
  }
}

export const ChatChannelRow = {
  extends: 'SidebarRow',
  attr: { 'data-cid': (el, s) => (s && s.id) || '' },
  background: (el, s) => {
    const root = s && s.root
    return root && s.id === root.activeChannelId ? 'white.1' : 'transparent'
  },
  color: (el, s) => {
    const root = s && s.root
    return root && s.id === root.activeChannelId ? 'title' : 'caption'
  },
  fontWeight: (el, s) => {
    const root = s && s.root
    return root && s.id === root.activeChannelId ? '600' : '400'
  },

  opacity: (el, s) => _isMuted(s) ? '.6' : '1',

  Icon: { tag: 'span', text: '#', color: 'caption' },
  Label: {
    text: (el, s) => (s && s.name) || 'channel',
    fontWeight: (el, s) => (_getUnread(s) > 0 && !_isMuted(s)) ? '700' : 'inherit'
  },
  MuteIndicator: _muteIndicator,
  UnreadBadge: _unreadBadge,
  MentionBadge: _mentionBadge,

  MoreBtn: {
    extends: 'ChatSidebarMore',
    items: (el, s) => [
      { label: 'Invite', action: 'invite-channel' },
      { label: 'Rename', action: 'rename-channel' },
      { label: 'Settings', action: 'channel-settings' },
      { label: 'Pin to top', action: 'pin-to-top' },
      { label: _isMuted(s) ? 'Unmute' : 'Mute', action: 'toggle-mute' }
    ]
  }
}

export const ChatDMRow = {
  extends: 'SidebarRow',
  gap: 'A',
  attr: {
    'data-cid': (el, s) => (s && s.id) || '',
    draggable: 'true'
  },
  opacity: (el, s) => _isMuted(s) ? '.6' : '1',
  background: (el, s) => {
    const root = s && s.root
    if (root && root._dragOverCid === s.id) return 'blue.05'
    return root && s.id === root.activeChannelId ? 'white.1' : 'transparent'
  },
  fontWeight: (el, s) => {
    const root = s && s.root
    return root && s.id === root.activeChannelId ? '600' : '400'
  },

  onDragStart: (e, el, s) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', s.id)
    s.root.update({ _dragSourceCid: s.id })
  },
  onDragEnd: (e, el, s) => {
    s.root.update({ _dragSourceCid: null, _dragOverCid: null })
  },
  onDragOver: (e, el, s) => {
    const src = s.root._dragSourceCid
    if (!src || src === s.id) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (s.root._dragOverCid !== s.id) s.root.update({ _dragOverCid: s.id })
  },
  onDragLeave: (e, el, s) => {
    if (s.root._dragOverCid === s.id) s.root.update({ _dragOverCid: null })
  },
  onDrop: async (e, el, s) => {
    e.preventDefault()
    const srcId = e.dataTransfer.getData('text/plain')
    s.root.update({ _dragSourceCid: null, _dragOverCid: null })
    if (!srcId || srcId === s.id) return

    // Collect all unique member emails from both DMs
    const me = s.root.currentUserEmail
    const members = s.root.channelMembers || []
    const srcEmails = members
      .filter((m) => m.channel_id === srcId && m.member_email !== me)
      .map((m) => m.member_email)
    const dstEmails = members
      .filter((m) => m.channel_id === s.id && m.member_email !== me)
      .map((m) => m.member_email)
    const allEmails = Array.from(new Set([...srcEmails, ...dstEmails]))
    if (allEmails.length < 2) return

    try {
      await el.call('createChannel', {
        kind: 'group_dm',
        memberEmails: allEmails
      })
    } catch (err) {
      console.error('[ChatDMRow] Group DM creation failed:', err)
    }
  },

  Icon: {
    tag: 'div',
    position: 'relative',
    flexShrink: '0',
    width: 'auto',
    height: 'auto',
    boxSize: 'auto',

    Avatar: {
      display: (el, s) => (s && s.kind === 'group_dm' ? 'none' : 'flex'),
      avatarType: 'initials',
      boxSize: 'A1 A1',
      src: (el, s) => {
        const root = s && s.root
        if (!root) return el.call('avatarUrl', null)
        const me = root.currentUserEmail
        const map = el.call('peopleByEmail')
        const other = (root.channelMembers || []).find(
          (m) => m.channel_id === s.id && m.member_email !== me
        )
        const otherEmail = other && other.member_email
        return el.call(
          'avatarUrl',
          (otherEmail && map[otherEmail]?.name) || otherEmail
        )
      }
    },

    AvatarBundle: {
      display: (el, s) => (s && s.kind === 'group_dm' ? 'flex' : 'none'),
      emails: (el, s) => {
        const root = s && s.root
        if (!root) return []
        const me = root.currentUserEmail
        return (root.channelMembers || [])
          .filter((m) => m.channel_id === s.id && m.member_email !== me)
          .map((m) => m.member_email)
          .slice(0, 3)
      }
    },

    OnlineDot: {
      hide: (el, s) => {
        if (s && s.kind === 'group_dm') return true
        const root = s && s.root
        if (!root || !root.presence) return true
        const me = root.currentUserEmail
        const other = (root.channelMembers || []).find(
          (m) => m.channel_id === s.id && m.member_email !== me
        )
        return !(other && root.presence[other.member_email])
      },
      position: 'absolute',
      bottom: '0',
      right: '0',
      width: 'Y',
      height: 'Y',
      round: '100%',
      background: 'green',
      borderWidth: '2px',
      borderColor: 'overlayStrong',
      borderStyle: 'solid'
    }
  },

  Label: {
    color: 'title',
    fontWeight: (el, s) => (_getUnread(s) > 0 && !_isMuted(s)) ? '700' : 'inherit',
    text: (el, s) => {
      const root = s && s.root
      if (!root) return ''
      return channelDisplayName(
        s,
        root.channelMembers || [],
        el.call('peopleByEmail'),
        root.currentUserEmail
      )
    }
  },
  MuteIndicator: _muteIndicator,
  UnreadBadge: _unreadBadge,
  MentionBadge: _mentionBadge,

  MoreBtn: {
    extends: 'ChatSidebarMore',
    items: (el, s) => {
      const muteItem = { label: _isMuted(s) ? 'Unmute' : 'Mute', action: 'toggle-mute' }
      if (s && s.kind === 'group_dm') {
        return [
          { label: 'Create channel', action: 'convert-to-channel' },
          { label: 'Rename', action: 'rename-channel' },
          { label: 'Pin to top', action: 'pin-to-top' },
          muteItem,
          { label: 'Hide', action: 'hide-dm' },
          { label: 'Leave', action: 'leave-channel', color: 'red' }
        ]
      }
      return [
        { label: 'Hide', action: 'hide-dm' },
        { label: 'Start group chat', action: 'start-group-chat' },
        { label: 'Pin to top', action: 'pin-to-top' },
        muteItem
      ]
    }
  }
}

// Group-chat row — same shape as ChatDMRow but renders first names only
// (no surnames) so multi-participant titles fit the sidebar width.
export const ChatGroupRow = {
  extends: 'ChatDMRow',
  Label: {
    color: 'title',
    fontWeight: (el, s) => _getUnread(s) > 0 ? '700' : 'inherit',
    text: (el, s) => {
      const root = s && s.root
      if (!root) return ''
      return channelDisplayName(
        s,
        root.channelMembers || [],
        el.call('peopleByEmail'),
        root.currentUserEmail,
        { firstOnly: true }
      )
    }
  }
}

// ── Three-dot dropdown for sidebar items ──

// Pinned row — adapts to channel kind (shows # for channels, avatar for DMs)
export const ChatPinnedRow = {
  extends: 'SidebarRow',
  attr: {
    'data-cid': (el, s) => (s && s.id) || '',
    draggable: 'true'
  },
  background: (el, s) => {
    const root = s && s.root
    return root && s.id === root.activeChannelId ? 'white.1' : 'transparent'
  },
  fontWeight: (el, s) => {
    const root = s && s.root
    return root && s.id === root.activeChannelId ? '600' : '400'
  },

  // SidebarRow base keys: Icon → Label → MoreBtn
  // For channels: # prefix. For DMs/groups: hidden (avatar in Label).
  Icon: {
    tag: 'span',
    icon: null,
    text: (el, s) => (s && s.kind === 'channel') ? '#' : '',
    color: 'caption',
    display: (el, s) => (s && s.kind === 'channel') ? 'inline' : 'none'
  },

  // Label includes avatar for DMs/groups, just text for channels
  Label: {
    flow: 'x',
    align: 'center left',
    gap: 'Z',
    flex: '1',
    minWidth: '0',

    // Single avatar for 1:1 DMs
    Avatar_dm: {
      tag: 'img',
      display: (el, s) => (s && s.kind === 'dm') ? 'block' : 'none',
      width: 'A1',
      height: 'A1',
      round: '100%',
      flexShrink: '0',
      src: (el, s) => {
        if (!s || s.kind !== 'dm') return ''
        const root = s && s.root
        if (!root) return ''
        const me = root.currentUserEmail
        const map = el.call('peopleByEmail')
        const other = (root.channelMembers || []).find(
          m => m.channel_id === s.id && m.member_email !== me
        )
        return el.call('avatarUrl', (other && map[other.member_email]?.name) || '')
      }
    },

    // Stacked avatars for group DMs
    AvatarBundle: {
      display: (el, s) => (s && s.kind === 'group_dm') ? 'flex' : 'none',
      flexShrink: '0',
      emails: (el, s) => {
        const root = s && s.root
        if (!root) return []
        const me = root.currentUserEmail
        return (root.channelMembers || [])
          .filter(m => m.channel_id === s.id && m.member_email !== me)
          .map(m => m.member_email)
          .slice(0, 3)
      }
    },

    Text: {
      tag: 'span',
      fontSize: 'Z2',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        if (!s) return ''
        if (s.kind === 'channel') return s.name || ''
        const root = s.root
        if (!root) return ''
        return channelDisplayName(
          s,
          root.channelMembers || [],
          el.call('peopleByEmail'),
          root.currentUserEmail
        )
      }
    }
  },

  MoreBtn: {
    extends: 'ChatSidebarMore'
  }
}

export const ChatSidebarMore = {
  extends: 'SidebarItemMore'
}

export const ChatSidebarMenuItem = {
  extends: 'SidebarItemMenuItem'
}
